import { lazy, Suspense, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setupAuthFetch } from './frontend/utils/apiFetch';
import { useAuth } from './frontend/components/AuthProvider';

/**
 * Wrapper component that sets up the auth-fetch interceptor.
 * Must be rendered inside BrowserRouter so useNavigate works.
 */
function AuthFetchInit(): null {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();

  useEffect(() => {
    setupAuthFetch(navigate, refreshSession);
  }, []);

  return null;
}

import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box } from '@mui/material';
import Login from './frontend/pages/Login';
import Welcome from './frontend/pages/Welcome';
import Team from './frontend/pages/Team';
import Billing from './frontend/pages/Billing';
import Tokens from './frontend/pages/Tokens';
import Models from './frontend/pages/Models';
import Home from './frontend/pages/Home';
// Lazy loaded components
const Wan22VideoGeneration = lazy(() => import('./frontend/pages/Wan22VideoGeneration'));
const ImageEdit = lazy(() => import('./frontend/pages/ImageEdit'));
const TextToImage = lazy(() => import('./frontend/pages/TextToImage'));
const ModelEditor = lazy(() => import('./frontend/pages/ModelEditor'));
const ProviderEditor = lazy(() => import('./frontend/pages/ProviderEditor'));
const Usage = lazy(() => import('./frontend/pages/Usage'));
const OrgEditor = lazy(() => import('./frontend/pages/OrgEditor'));
const NextScene = lazy(() => import('./frontend/pages/NextScene'));
const LtxVideo = lazy(() => import('./frontend/pages/LtxVideo'));
const SeedanceVideo = lazy(() => import('./frontend/pages/SeedanceVideo'));
const VideoAddSound = lazy(() => import('./frontend/pages/VideoAddSound'));
const LtxVideoExtend = lazy(() => import('./frontend/pages/LtxVideoExtend'));
const MultiAngle = lazy(() => import('./frontend/pages/MultiAngle'));
const HumanPortrait = lazy(() => import('./frontend/pages/HumanPortrait'));
const GenerationHistory = lazy(() => import('./frontend/pages/GenerationHistory'));
const FaceSwap = lazy(() => import('./frontend/pages/FaceSwap'));
const InfluencerManager = lazy(() => import('./frontend/pages/InfluencerManager'));
import { ProtectedRoute } from './frontend/components/ProtectedRoute';
import { AuthProvider } from './frontend/components/AuthProvider';
import { Layout } from './frontend/components/Layout';
import { I18nProvider } from './frontend/i18n';
import theme from './theme';

// Loading fallback component - renders in content area, not full screen
const LoadingFallback = () => (
  <Layout title="Loading...">
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        width: '100%',
      }}
    >
      <CircularProgress />
    </Box>
  </Layout>
);

function App(): JSX.Element {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <AuthFetchInit />
            <Routes>
            {/* Public route - redirects to appropriate page if authenticated */}
            <Route
              path="/login"
              element={
                <ProtectedRoute requireAuth={false}>
                  <Login />
                </ProtectedRoute>
              }
            />
            {/* Protected route - requires auth but no org */}
            <Route
              path="/welcome"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={false}>
                  <Welcome />
                </ProtectedRoute>
              }
            />
            {/* Protected route - requires both auth and org */}
            <Route
              path="/team"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true} requireAdminForNonPipelet={true}>
                  <Team />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true} requireAdminForNonPipelet={true}>
                  <Billing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tokens"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Tokens />
                </ProtectedRoute>
              }
            />
            <Route
              path="/models"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Models />
                </ProtectedRoute>
              }
            />
            <Route
              path="/model-editor"
              element={
                <ProtectedRoute requireAuth={true} requireSystem={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <ModelEditor />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/model-editor/*"
              element={
                <ProtectedRoute requireAuth={true} requireSystem={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <ModelEditor />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/org-editor"
              element={
                <ProtectedRoute requireAuth={true} requireSystem={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <OrgEditor />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/provider-editor"
              element={
                <ProtectedRoute requireAuth={true} requireSystem={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <ProviderEditor />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/usage"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <Usage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing/success"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true} requireAdminForNonPipelet={true}>
                  <Billing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing/cancel"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true} requireAdminForNonPipelet={true}>
                  <Billing />
                </ProtectedRoute>
              }
            />
            {/* Wan 2.2 Video Generation route */}
            <Route
              path="/wan22-video"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <Wan22VideoGeneration />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Image Edit route */}
            <Route
              path="/image-edit"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <ImageEdit />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Text to Image route */}
            <Route
              path="/text-to-image"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <TextToImage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Next Scene route */}
            <Route
              path="/next-scene"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <NextScene />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Seedance Video route */}
            <Route
              path="/seedance-video"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <SeedanceVideo />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* LTX Video route */}
            <Route
              path="/ltx-video"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <LtxVideo />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Video Add Sound route */}
            <Route
              path="/video-add-sound"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <VideoAddSound />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* LTX Video Extension route */}
            <Route
              path="/ltx-video-extend"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <LtxVideoExtend />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Multi-Angle View route */}
            <Route
              path="/multi-angle"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <MultiAngle />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Human Portrait route */}
            <Route
              path="/human-portrait"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <HumanPortrait />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Face Swap route */}
            <Route
              path="/face-swap"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <FaceSwap />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Influencer Manager route */}
            <Route
              path="/influencer-manager"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <InfluencerManager />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Generation History route */}
            <Route
              path="/history"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Suspense fallback={<LoadingFallback />}>
                    <GenerationHistory />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Root route - always redirects based on auth/org state */}
            <Route
              path="/"
              element={
                <ProtectedRoute requireAuth={true} requireOrg={true}>
                  <Home />
                </ProtectedRoute>
              }
            />
          </Routes>
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
